import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Typography,
  Space,
  Button,
  Input,
  Select,
  Tag,
  Badge,
  Avatar,
  Modal,
  Form,
  Tooltip,
  Row,
  Col,
  Statistic,
  Popconfirm,
  Drawer,
  Descriptions,
  Divider,
  Switch,
  App as AntApp,
  Empty,
  Alert,
} from 'antd';
import {
  CrownOutlined,
  BankOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SwapOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
  ApartmentOutlined,
  TeamOutlined,
  GlobalOutlined,
  LinkOutlined,
  BranchesOutlined,
  ClusterOutlined,
  SafetyCertificateOutlined,
  CarOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './SuperAdminTenants.css';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const SuperAdminTenants = () => {
  const { user, updateUser } = useAuth();
  const { message } = AntApp.useApp();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);

  // Filtros
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [groupFilter, setGroupFilter] = useState('ALL');

  // Modais e Drawers
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [logoPreview, setLogoPreview] = useState('');

  // Drawer de Detalhes
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const activeTenantId = user?.organizationId || user?.organization?.id;

  const formatCnpj = (val) => {
    if (!val) return '-';
    const clean = String(val).replace(/\D/g, '');
    if (clean.length === 14) {
      return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    return val;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const copyToClipboard = (text, label = 'Texto') => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success(`${label} copiado!`);
  };

  const loadTenants = async (showMsg = false) => {
    try {
      setLoading(true);
      const data = await api.organizations.getAllForSuperAdmin();
      setTenants(Array.isArray(data) ? data : []);
      if (showMsg) message.success('Lista de organizações atualizada');
    } catch (e) {
      message.error(e.message || 'Erro ao carregar organizações para o Super Admin');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  // Selecionar / Trocar Tenant (Impersonate / Switch Tenant)
  const handleSelectTenant = async (tenantId) => {
    if (tenantId === activeTenantId) {
      message.info('Você já está operando nesta organização.');
      return;
    }
    try {
      setSwitchingId(tenantId);
      const res = await api.organizations.selectOrganization(tenantId);
      if (res?.user) {
        updateUser({
          organizationId: res.user.organizationId,
          organization: res.user.organization,
        });
      }
      message.success(res?.message || 'Organização selecionada com sucesso');
      await loadTenants();
    } catch (e) {
      message.error(e.message || 'Erro ao alternar para a organização');
    } finally {
      setSwitchingId(null);
    }
  };

  // Alternar Status do Tenant (Ativar / Desativar)
  const handleToggleStatus = async (tenant) => {
    const nextStatus = !tenant.isActive;
    try {
      await api.organizations.update(tenant.id, { isActive: nextStatus });
      message.success(`Organização "${tenant.name}" foi ${nextStatus ? 'ativada' : 'inativada'}`);
      setTenants((prev) =>
        prev.map((t) => (t.id === tenant.id ? { ...t, isActive: nextStatus } : t))
      );
      if (selectedTenant?.id === tenant.id) {
        setSelectedTenant((prev) => ({ ...prev, isActive: nextStatus }));
      }
    } catch (e) {
      message.error(e.message || 'Erro ao alterar status da organização');
    }
  };

  // Abrir Modal de Criação / Edição
  const handleOpenModal = (tenant = null) => {
    setEditingTenant(tenant);
    if (tenant) {
      setLogoPreview(tenant.logoUrl || '');
      form.setFieldsValue({
        name: tenant.name,
        cnpj: tenant.cnpj || '',
        dominio: tenant.dominio || '',
        website: tenant.website || '',
        logoUrl: tenant.logoUrl || '',
        sswBaseUrl: tenant.sswBaseUrl || '',
        isActive: tenant.isActive ?? true,
      });
    } else {
      setLogoPreview('');
      form.resetFields();
      form.setFieldsValue({
        isActive: true,
        sswBaseUrl: 'https://sistema.ssw.inf.br/bin/ssw0422',
      });
    }
    setModalOpen(true);
  };

  // Salvar Organização (Criar ou Atualizar)
  const handleSaveTenant = async (values) => {
    try {
      setSaving(true);
      const payload = {
        name: values.name,
        cnpj: values.cnpj || undefined,
        dominio: values.dominio || undefined,
        website: values.website || undefined,
        logoUrl: values.logoUrl || undefined,
        sswBaseUrl: values.sswBaseUrl || undefined,
        isActive: values.isActive,
      };

      if (editingTenant) {
        await api.organizations.update(editingTenant.id, payload);
        message.success('Organização atualizada com sucesso');
      } else {
        await api.organizations.create(payload);
        message.success('Nova organização provisionada com sucesso');
      }

      setModalOpen(false);
      await loadTenants();
    } catch (e) {
      message.error(e.message || 'Erro ao salvar organização');
    } finally {
      setSaving(false);
    }
  };

  // Excluir Tenant
  const handleDeleteTenant = async (tenantId) => {
    try {
      await api.organizations.delete(tenantId);
      message.success('Organização removida com sucesso');
      await loadTenants();
      if (selectedTenant?.id === tenantId) setDrawerOpen(false);
    } catch (e) {
      message.error(e.message || 'Não foi possível remover a organização (verifique se há dados vinculados)');
    }
  };

  // Filtragem e Métricas
  const filteredTenants = useMemo(() => {
    return tenants.filter((t) => {
      // Busca por texto
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        t.name?.toLowerCase().includes(q) ||
        t.cnpj?.toLowerCase().includes(q) ||
        t.dominio?.toLowerCase().includes(q) ||
        t.id?.toLowerCase().includes(q);

      // Filtro de status
      const matchStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'ACTIVE' && t.isActive) ||
        (statusFilter === 'INACTIVE' && !t.isActive);

      // Filtro de grupo
      const matchGroup =
        groupFilter === 'ALL' ||
        (groupFilter === 'GROUPED' && !!t.organization_groups) ||
        (groupFilter === 'STANDALONE' && !t.organization_groups);

      return matchSearch && matchStatus && matchGroup;
    });
  }, [tenants, search, statusFilter, groupFilter]);

  // Estatísticas Globais
  const stats = useMemo(() => {
    const total = tenants.length;
    const active = tenants.filter((t) => t.isActive).length;
    const inactive = total - active;
    const totalBranches = tenants.reduce((acc, t) => acc + (t._count?.branches || 0), 0);
    const totalUsers = tenants.reduce((acc, t) => acc + (t._count?.users || 0), 0);
    return { total, active, inactive, totalBranches, totalUsers };
  }, [tenants]);

  // Colunas da Tabela
  const columns = [
    {
      title: 'Organização / Tenant',
      dataIndex: 'name',
      key: 'name',
      width: 280,
      render: (name, record) => (
        <div className="sat-tenant-cell">
          <Avatar
            size={42}
            shape="square"
            src={record.logoUrl}
            icon={<BankOutlined />}
            className="sat-tenant-avatar"
            style={{
              backgroundColor: record.isActive ? '#1677ff' : '#8c8c8c',
              fontWeight: 600,
            }}
          >
            {name ? name.substring(0, 2).toUpperCase() : 'OR'}
          </Avatar>
          <div className="sat-tenant-info">
            <Space size={6} align="center">
              <Text strong className="sat-tenant-name">{name}</Text>
              {record.id === activeTenantId && (
                <Tag color="gold" style={{ fontSize: 11, fontWeight: 600, margin: 0 }}>
                  <CheckOutlined /> Atual
                </Tag>
              )}
            </Space>
            <Space size={4}>
              <Text type="secondary" code style={{ fontSize: 11 }}>
                {record.id ? `${record.id.slice(0, 8)}...` : '-'}
              </Text>
              <Tooltip title="Copiar Tenant ID">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined style={{ fontSize: 11 }} />}
                  onClick={() => copyToClipboard(record.id, 'Tenant ID')}
                />
              </Tooltip>
            </Space>
          </div>
        </div>
      ),
    },
    {
      title: 'CNPJ',
      dataIndex: 'cnpj',
      key: 'cnpj',
      width: 160,
      render: (cnpj) => (
        <Text style={{ fontSize: 13 }}>{cnpj ? formatCnpj(cnpj) : <Text type="secondary">-</Text>}</Text>
      ),
    },
    {
      title: 'Domínio ERP',
      dataIndex: 'dominio',
      key: 'dominio',
      width: 130,
      render: (dominio) =>
        dominio ? (
          <Tag color="cyan" icon={<BranchesOutlined />} style={{ fontWeight: 600 }}>
            {dominio}
          </Tag>
        ) : (
          <Tag color="default">Não config.</Tag>
        ),
    },
    {
      title: 'Grupo Corporativo',
      dataIndex: 'organization_groups',
      key: 'group',
      width: 180,
      render: (group) =>
        group ? (
          <Tag color="purple" icon={<ClusterOutlined />} style={{ fontWeight: 500 }}>
            {group.name}
          </Tag>
        ) : (
          <Tag color="default">Independente</Tag>
        ),
    },
    {
      title: 'Recursos',
      key: 'resources',
      width: 170,
      render: (_, record) => (
        <Space size={6} wrap>
          <Tooltip title="Filiais vinculadas">
            <Tag color="blue" icon={<ApartmentOutlined />}>
              {record._count?.branches || 0}
            </Tag>
          </Tooltip>
          <Tooltip title="Usuários com acesso">
            <Tag color="green" icon={<TeamOutlined />}>
              {record._count?.users || 0}
            </Tag>
          </Tooltip>
          {(record._count?.veiculos || 0) > 0 && (
            <Tooltip title="Veículos cadastrados">
              <Tag color="orange" icon={<CarOutlined />}>
                {record._count.veiculos}
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (active, record) => (
        <Popconfirm
          title={`${active ? 'Inativar' : 'Ativar'} organização?`}
          description={`Deseja realmente ${active ? 'inativar o acesso de' : 'reativar'} "${record.name}"?`}
          onConfirm={() => handleToggleStatus(record)}
          okText="Confirmar"
          cancelText="Cancelar"
        >
          <Switch
            checked={active}
            checkedChildren="Ativa"
            unCheckedChildren="Inativa"
            style={{ backgroundColor: active ? '#52c41a' : undefined }}
          />
        </Popconfirm>
      ),
    },
    {
      title: 'Cadastro',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => <Text type="secondary" style={{ fontSize: 12 }}>{formatDate(date)}</Text>,
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 190,
      fixed: 'right',
      render: (_, record) => {
        const isCurrent = record.id === activeTenantId;
        return (
          <Space size={4}>
            {/* Botão de Assumir / Selecionar Tenant */}
            <Tooltip title={isCurrent ? 'Organização ativa no momento' : 'Operar como este Tenant'}>
              <Button
                type={isCurrent ? 'default' : 'primary'}
                size="small"
                icon={<SwapOutlined />}
                disabled={isCurrent || !record.isActive}
                loading={switchingId === record.id}
                onClick={() => handleSelectTenant(record.id)}
              >
                {isCurrent ? 'Ativa' : 'Acessar'}
              </Button>
            </Tooltip>

            {/* Visualizar Raio-X */}
            <Tooltip title="Visualizar Detalhes">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => {
                  setSelectedTenant(record);
                  setDrawerOpen(true);
                }}
              />
            </Tooltip>

            {/* Editar Tenant */}
            <Tooltip title="Editar Configurações">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleOpenModal(record)}
              />
            </Tooltip>

            {/* Excluir Tenant */}
            <Popconfirm
              title="Excluir Organização"
              description={`Tem certeza que deseja excluir "${record.name}"? Esta ação é irreversível.`}
              onConfirm={() => handleDeleteTenant(record.id)}
              okText="Sim, excluir"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Excluir">
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="super-admin-tenants-container">
      {/* Cabeçalho com Destaque Super Admin */}
      <Card className="sat-header-card">
        <div className="sat-header-flex">
          <div className="sat-header-title-box">
            <Avatar size={54} icon={<CrownOutlined />} className="sat-crown-avatar" />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Title level={2} style={{ margin: 0 }}>Gestão Global de Tenants</Title>
                <Tag color="gold" style={{ fontSize: 12, padding: '2px 10px', borderRadius: 12 }}>
                  SUPER_ADMIN
                </Tag>
              </div>
              <Text type="secondary">
                Centro de controle multi-tenant para gerenciamento, provisionamento e monitoramento de todas as organizações.
              </Text>
            </div>
          </div>

          <Space wrap align="center">
            {activeTenantId && (
              <div className="sat-active-tenant-banner">
                <BankOutlined style={{ color: '#1677ff' }} />
                <span>Tenant em operação: <strong>{user?.organization?.name || activeTenantId}</strong></span>
              </div>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadTenants(true)}
              loading={loading}
            >
              Atualizar
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
              style={{ backgroundColor: '#1677ff' }}
            >
              Novo Tenant
            </Button>
          </Space>
        </div>
      </Card>

      {/* Cards de Métricas Globais da Plataforma */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card className="sat-kpi-card" bordered={false}>
            <Statistic
              title="Total de Tenants"
              value={stats.total}
              prefix={<BankOutlined style={{ color: '#1677ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={5}>
          <Card className="sat-kpi-card" bordered={false}>
            <Statistic
              title="Tenants Ativos"
              value={stats.active}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={5}>
          <Card className="sat-kpi-card" bordered={false}>
            <Statistic
              title="Tenants Inativos"
              value={stats.inactive}
              prefix={<StopOutlined style={{ color: '#ff4d4f' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={5}>
          <Card className="sat-kpi-card" bordered={false}>
            <Statistic
              title="Total de Filiais"
              value={stats.totalBranches}
              prefix={<ApartmentOutlined style={{ color: '#722ed1' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={5}>
          <Card className="sat-kpi-card" bordered={false}>
            <Statistic
              title="Total de Usuários"
              value={stats.totalUsers}
              prefix={<TeamOutlined style={{ color: '#fa8c16' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Tabela de Tenants */}
      <Card className="sat-table-card">
        {/* Barra de Busca e Filtros */}
        <Row gutter={[16, 16]} align="middle" justify="space-between" style={{ marginBottom: 20 }}>
          <Col xs={24} sm={12} md={10}>
            <Input
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Buscar por nome, CNPJ, domínio SSW ou ID..."
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="middle"
            />
          </Col>
          <Col xs={24} sm={12} md={14}>
            <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 140 }}
                placeholder="Status"
              >
                <Option value="ALL">Todos os status</Option>
                <Option value="ACTIVE">Apenas Ativos</Option>
                <Option value="INACTIVE">Apenas Inativos</Option>
              </Select>

              <Select
                value={groupFilter}
                onChange={setGroupFilter}
                style={{ width: 180 }}
                placeholder="Grupo Corporativo"
              >
                <Option value="ALL">Todos os grupos</Option>
                <Option value="GROUPED">Com Grupo Corporativo</Option>
                <Option value="STANDALONE">Independentes</Option>
              </Select>

              <Text type="secondary" style={{ fontSize: 13 }}>
                Exibindo <strong>{filteredTenants.length}</strong> de <strong>{tenants.length}</strong> tenants
              </Text>
            </Space>
          </Col>
        </Row>

        <Table
          dataSource={filteredTenants}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1350 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total) => `Total: ${total} organizações`,
          }}
        />
      </Card>

      {/* Modal de Criação / Edição de Organização */}
      <Modal
        open={modalOpen}
        title={
          <Space>
            <BankOutlined style={{ color: '#1677ff' }} />
            <span>{editingTenant ? 'Editar Organização / Tenant' : 'Nova Organização / Tenant'}</span>
          </Space>
        }
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={680}
        style={{ top: 0 }}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveTenant}
          style={{ marginTop: 16 }}
        >
          <Row gutter={16}>
            <Col xs={24} md={14}>
              <Form.Item
                name="name"
                label="Razão Social / Nome da Organização"
                rules={[{ required: true, message: 'Nome da organização é obrigatório' }]}
              >
                <Input placeholder="Ex: Diálogo Logística Inteligente" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item
                name="cnpj"
                label="CNPJ"
                tooltip="14 dígitos (numérico ou novo padrão alfanumérico 2026)"
              >
                <Input placeholder="Ex: 00.000.000/0001-00" size="large" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="dominio"
                label="Domínio ERP SSW"
                tooltip="Sigla/código de identificação no ERP SSW (ex: DLG, CLD)"
              >
                <Input placeholder="Ex: DLG" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item
                name="website"
                label="Website Institucional"
              >
                <Input
                  prefix={<GlobalOutlined style={{ color: '#bfbfbf' }} />}
                  placeholder="https://suaempresa.com.br"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="sswBaseUrl"
            label="URL de Login SSW"
            tooltip="Endpoint de autenticação da sessão no ERP SSW"
          >
            <Input
              placeholder="https://sistema.ssw.inf.br/bin/ssw0422"
              size="large"
            />
          </Form.Item>

          <Row gutter={16} align="middle">
            <Col xs={24} md={18}>
              <Form.Item
                name="logoUrl"
                label="URL da Logomarca"
                tooltip="URL pública da imagem de logotipo (PNG, SVG ou JPEG)"
              >
                <Input
                  prefix={<LinkOutlined style={{ color: '#bfbfbf' }} />}
                  placeholder="https://.../logo.png"
                  size="large"
                  onChange={(e) => setLogoPreview(e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6} style={{ textAlign: 'center', marginBottom: 16 }}>
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                Prévia do Logo
              </Text>
              <Avatar
                size={48}
                shape="square"
                src={logoPreview}
                icon={<BankOutlined />}
                style={{ backgroundColor: '#1677ff', borderRadius: 8 }}
              />
            </Col>
          </Row>

          <Form.Item
            name="isActive"
            label="Situação Cadastral"
            valuePropName="checked"
          >
            <Switch checkedChildren="Tenant Ativo" unCheckedChildren="Tenant Bloqueado" />
          </Form.Item>

          <Divider style={{ margin: '16px 0' }} />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              style={{ backgroundColor: '#1677ff' }}
            >
              {editingTenant ? 'Salvar Alterações' : 'Provisionar Tenant'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Drawer de Detalhes / Raio-X do Tenant */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={
          <Space>
            <BankOutlined style={{ color: '#1677ff' }} />
            <span>Raio-X do Tenant: {selectedTenant?.name}</span>
          </Space>
        }
        width={560}
      >
        {selectedTenant && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Cabeçalho do Tenant no Drawer */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px', background: 'rgba(0,0,0,0.02)', borderRadius: 10 }}>
              <Avatar
                size={64}
                shape="square"
                src={selectedTenant.logoUrl}
                icon={<BankOutlined />}
                style={{ backgroundColor: selectedTenant.isActive ? '#1677ff' : '#8c8c8c', borderRadius: 10 }}
              >
                {selectedTenant.name?.substring(0, 2).toUpperCase()}
              </Avatar>
              <div>
                <Title level={4} style={{ margin: 0 }}>{selectedTenant.name}</Title>
                <Space size={6} style={{ marginTop: 4 }}>
                  <Tag color={selectedTenant.isActive ? 'green' : 'red'}>
                    {selectedTenant.isActive ? 'Ativo' : 'Inativo'}
                  </Tag>
                  {selectedTenant.dominio && (
                    <Tag color="cyan">ERP: {selectedTenant.dominio}</Tag>
                  )}
                </Space>
              </div>
            </div>

            {/* Ação rápida para operar como este tenant */}
            <Alert
              message="Acesso Rápido de Super Administrador"
              description="Você pode alternar sua sessão atual para gerenciar os dados, filiais e rotas sob a ótica desta organização."
              type="info"
              showIcon
              action={
                <Button
                  type="primary"
                  size="small"
                  icon={<SwapOutlined />}
                  disabled={selectedTenant.id === activeTenantId || !selectedTenant.isActive}
                  loading={switchingId === selectedTenant.id}
                  onClick={() => handleSelectTenant(selectedTenant.id)}
                >
                  {selectedTenant.id === activeTenantId ? 'Tenant Atual' : 'Operar Neste Tenant'}
                </Button>
              }
            />

            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Identificador (UUID)">
                <Space>
                  <Text code>{selectedTenant.id}</Text>
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(selectedTenant.id, 'UUID')}
                  />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="CNPJ">
                {selectedTenant.cnpj ? formatCnpj(selectedTenant.cnpj) : 'Não informado'}
              </Descriptions.Item>
              <Descriptions.Item label="Domínio SSW">
                {selectedTenant.dominio || 'Não configurado'}
              </Descriptions.Item>
              <Descriptions.Item label="Website">
                {selectedTenant.website ? (
                  <a href={selectedTenant.website} target="_blank" rel="noopener noreferrer">
                    {selectedTenant.website}
                  </a>
                ) : (
                  'Não informado'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="URL Base SSW">
                <Text code>{selectedTenant.sswBaseUrl || 'https://sistema.ssw.inf.br/bin/ssw0422'}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Grupo Corporativo">
                {selectedTenant.organization_groups ? (
                  <Space>
                    <Tag color="purple">{selectedTenant.organization_groups.name}</Tag>
                    <Text type="secondary" code style={{ fontSize: 11 }}>
                      ID: {selectedTenant.organization_groups.id}
                    </Text>
                  </Space>
                ) : (
                  <Text type="secondary">Nenhum (Tenant Independente)</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Data de Cadastro">
                {formatDate(selectedTenant.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Última Modificação">
                {formatDate(selectedTenant.updatedAt)}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ margin: '16px 0 8px 0' }}>Volumetria do Tenant</Title>
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="Filiais"
                    value={selectedTenant._count?.branches || 0}
                    prefix={<ApartmentOutlined style={{ color: '#1677ff' }} />}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="Usuários"
                    value={selectedTenant._count?.users || 0}
                    prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="Colaboradores"
                    value={selectedTenant._count?.employees || 0}
                    prefix={<SafetyCertificateOutlined style={{ color: '#722ed1' }} />}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="Veículos"
                    value={selectedTenant._count?.veiculos || 0}
                    prefix={<CarOutlined style={{ color: '#fa8c16' }} />}
                  />
                </Card>
              </Col>
            </Row>

            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  setDrawerOpen(false);
                  handleOpenModal(selectedTenant);
                }}
                block
              >
                Editar Organização
              </Button>
            </div>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default SuperAdminTenants;
